import { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { borrowerActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { ValidationMessagesModal, BfiCustomStore } from '../../../common/SharedComponents';
import { LoadPanel } from 'devextreme-react/load-panel';
import { BorrowerApi, ListApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { RefreshEvents } from '../../../common/SharedEvents';
import { handleAdminError, handleAdminSuccess } from '../../../common/SharedFunctions';
import { DetailsTab } from '../components/DetailsTab';
import { LinkedEntitiesTab } from './components/LinkedEntitiesTab';
import TabPanel from 'devextreme-react/tab-panel';
import { ApplicationTab } from './components/ApplicationTab';
import { BClientTab } from './components/BClientTab';
import { UserAgreementsTab } from './components/UserAgreementsTab';
import { CCLTab } from './components/CCLTab';

const EditPrincipalModal = () => {
    const { isOpen = false, principalId, onChange, entityId = 0 } = useSelector((state) => state.borrower.editPrincipalModal);
    const { dispatch } = store;
    const borrowerApi = new BorrowerApi.BorrowerApi(applicationInsights);
    const listApi = new ListApi.ListApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const applicationsStore = new BfiCustomStore();
    const bClientsStore = new BfiCustomStore();
    const linkStore = new BfiCustomStore();

    const tabNames = [
        'Details',
        entityId ? 'Other Linked Entities' : 'Linked Entities',
        'Applications',
        'B-Client',
        'User Agreements',
    ];
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);

    //Employees
    const [employees, set_employees] = useState([]);

    //Principal Info
    const [firstName, set_firstName] = useState('');
    const [middleName, set_middleName] = useState('');
    const [lastName, set_lastName] = useState('');
    const [suffix, set_suffix] = useState('');
    const [street1, set_street1] = useState('');
    const [street2, set_street2] = useState('');
    const [city, set_city] = useState('');
    const [state, set_state] = useState('');
    const [zipCode, set_zipcode] = useState('');
    const [workPhone, set_workPhone] = useState('');
    const [mobilePhone, set_mobilePhone] = useState('');
    const [email, set_email] = useState('');
    const [secondaryEmail, set_secondaryEmail] = useState('');
    const [netWorth, set_netWorth] = useState('');
    const [adjustedNetWorth, set_adjustedNetWorth] = useState('');
    const [validation, set_validation] = useState(null);

    //BFI POC Info
    const [userResponsibleId, set_userResponsibleId] = useState('');

    //Link info
    const [percentOwnership, set_percentOwnership] = useState(null);
    const [isOfficer, set_isOfficer] = useState(false);
    const [linkTitle, set_linkTitle] = useState('');
    const [entityName, set_entityName] = useState('');

    // CCL
    const [isCCL_Borrower, set_isCCL_Borrower] = useState(true);
    const [requireTermsheet, set_requireTermsheet] = useState(true);
    const [requireUnderwritingFee, set_requireUnderwritingFee] = useState(true);
    const [requireValuationFee, set_requireValuationFee] = useState(true);

    useEffect(() => {
        if (isCCL_Borrower) return;

        set_requireTermsheet(true);
        set_requireUnderwritingFee(true);
        set_requireValuationFee(true);
    }, [isCCL_Borrower]);

    //Dialogs
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const handleClose = () =>
        dispatch(
            borrowerActions.setEditPrincipalModal({
                editPrincipalModal: { isOpen: false },
            })
        );

    const styles = {
        buttonContainer: {
            marginRight: '10px',
            marginLeft: '10px',
            textAlign: 'right',
        },
        buttonMarginRight: { marginRight: '10px' },
        sectionHeader: { margin: '10px 0' },
        formColumn: { paddingBottom: '5px', paddingRight: '5px' },
        isofficer: { paddingTop: '30px' },
    };

    useEffect(() => {
        loadBfiEmployees();
    }, []);

    useEffect(() => {
        async function principalIdChanged() {
            try {
                if (principalId) {
                    const response = await borrowerApi.getPrincipalById(principalId);
                    if (response.statusCode !== 200) return;
                    const principal = response.result;
                    setPrincipalState(principal);
                } else {
                    setPrincipalState(null);
                }

                if (entityId) {
                    const response2 = await borrowerApi.getEntityPrincipalLink(entityId, principalId);
                    if (response2.statusCode !== 200) {
                        return;
                    }
                    const link = response2.result;
                    setLinkState(link);
                } else {
                    setLinkState(null);
                }
            } finally {
                set_loadingOpen(false);
            }
        }

        principalIdChanged();
    }, [principalId, entityId]);

    const loadBfiEmployees = async () => {
        const data = await listApi.getBfiEmployees();
        set_employees(data);
    };

    const validatePrincipal = async () => {
        //Validate names
        set_loadingMessage('Validating...');
        try {
            const model = {
                id: principalId,
                email,
                firstName,
                middleName,
                lastName,
                suffix,
            };
            const response = await borrowerApi.validatePrincipal(model);
            if (response.statusCode !== 200 || !response.result) {
                set_loadingOpen(false);
                return false;
            }

            if (response.result.hasErrors || response.result.hasWarnings) {
                set_validation(response.result);
                set_loadingOpen(false);
                return false;
            }

            return true;
        } catch (ex) {
            set_loadingOpen(false);
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
            return false;
        }
    };

    const validateAndSave = async () => {
        var isOk = await validatePrincipal();
        if (!isOk) {
            return;
        }

        await savePrincipal();
    };

    const savePrincipal = async () => {
        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                id: principalId,
                firstName,
                middleName,
                lastName,
                suffix,
                workPhone,
                mobilePhone,
                email,
                secondaryEmail,
                businessAddress: {
                    street1,
                    street2,
                    city,
                    state,
                    zipCode,
                },
                netWorth,
                adjustedNetWorth,
                userResponsibleId: !userResponsibleId || userResponsibleId === '' ? null : userResponsibleId,
                requireTermsheet,
                requireUnderwritingFee,
                requireValuationFee,
            };

            if (entityId) {
                model.link = {
                    entityId,
                    percentOwnership,
                    isOfficer,
                    title: linkTitle,
                };
            }

            set_loadingMessage('Saving changes...');
            const response = await borrowerApi.savePrincipal(model);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            if (response.result.hasErrors || response.result.hasWarnings) {
                set_validation(response.result);
                set_loadingOpen(false);
                return false;
            }

            refreshEvents.dispatchRefreshEvent();

            const savedPrincipal = response.result;
            setPrincipalState(savedPrincipal);
            onChange && onChange(savedPrincipal);

            handleAdminSuccess('Principal Saved successfully.');
            set_loadingOpen(false);

            setTimeout(() => {
                handleClose();
            }, 600);
        } catch (ex) {
            set_loadingOpen(false);
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        }
    };

    const onValidationConfirm = async () => {
        set_validation(null);
        await savePrincipal();
    };

    const onValidationCancel = () => {
        set_validation(null);
    };

    const resetPrincipalState = () => {
        set_firstName('');
        set_middleName('');
        set_lastName('');
        set_suffix('');
        set_email('');
        set_secondaryEmail('');
        set_workPhone('');
        set_mobilePhone('');
        set_street1('');
        set_street2('');
        set_city('');
        set_state('');
        set_zipcode('');
        set_netWorth(null);
        set_adjustedNetWorth(null);
        set_userResponsibleId('');
    };

    const resetLinkState = () => {
        set_percentOwnership('');
        set_linkTitle('');
        set_isOfficer(false);
        set_entityName('');
    };

    const setPrincipalState = (principal) => {
        if (principal) {
            set_firstName(principal.firstName);
            set_middleName(principal.middleName);
            set_lastName(principal.lastName);
            set_suffix(principal.suffix);
            set_email(principal.email);
            set_secondaryEmail(principal.secondaryEmail);
            set_workPhone(principal.workPhone);
            set_mobilePhone(principal.mobilePhone);
            set_street1(principal.businessAddress?.street1);
            set_street2(principal.businessAddress?.street2);
            set_city(principal.businessAddress?.city);
            set_state(principal.businessAddress?.state);
            set_zipcode(principal.businessAddress?.zipCode);
            set_netWorth(principal?.netWorth);
            set_adjustedNetWorth(principal?.adjustedNetWorth);
            set_userResponsibleId(principal?.userResponsibleId ?? '');

            set_requireTermsheet(principal?.requireTermsheet);
            set_requireValuationFee(principal?.requireValuationFee);
            set_requireUnderwritingFee(principal?.requireUnderwritingFee);
        } else {
            resetPrincipalState();
        }
    };

    const setLinkState = (link) => {
        if (link) {
            set_percentOwnership(link.percentOwnership);
            set_linkTitle(link.title);
            set_isOfficer(link.isOfficer);
            set_entityName(link.entityName);

            set_requireTermsheet(link?.requireTermsheet);
            set_requireValuationFee(link?.requireValuationFee);
            set_requireUnderwritingFee(link?.set_requireUnderwritingFee);
        } else {
            resetLinkState();
        }
    };

    const onTabChanged = (e) => {
        setSelectedTabIndex(tabNames.indexOf(e.addedItems[0].title));
    };

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='lg'
                open={isOpen}
                onClose={handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container principal-modal'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '1100px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form
                                    name='editBorrowerForm'
                                    // onSubmit={(event) => validateAndSave(event)}
                                    className={'standard-form'}
                                >
                                    <div
                                        className='page-header'
                                        role='banner'
                                    >
                                        <h1
                                            style={{
                                                height: '50px',
                                                marginTop: '15px',
                                            }}
                                        >
                                            {principalId ? 'Edit Borrower Principal' : 'Add Borrower Principal'}
                                        </h1>
                                        <button
                                            type='button'
                                            className='close'
                                            data-dismiss='modal'
                                            aria-label='Close'
                                            onClick={handleClose}
                                        >
                                            <span aria-hidden='true'>&times;</span>
                                        </button>
                                    </div>

                                    <div
                                        className='scrollable-content'
                                        style={{ margin: '10px' }}
                                    >
                                        <TabPanel onSelectionChanged={onTabChanged}>
                                            {DetailsTab({
                                                principalId,
                                                entityId,
                                                entityName,
                                                firstName,
                                                set_firstName,
                                                middleName,
                                                set_middleName,
                                                lastName,
                                                set_lastName,
                                                suffix,
                                                set_suffix,
                                                street1,
                                                set_street1,
                                                street2,
                                                set_street2,
                                                city,
                                                set_city,
                                                state,
                                                set_state,
                                                zipCode,
                                                set_zipcode,
                                                workPhone,
                                                set_workPhone,
                                                mobilePhone,
                                                set_mobilePhone,
                                                email,
                                                set_email,
                                                secondaryEmail,
                                                set_secondaryEmail,
                                                userResponsibleId,
                                                set_userResponsibleId,
                                                netWorth,
                                                set_netWorth,
                                                adjustedNetWorth,
                                                set_adjustedNetWorth,
                                                percentOwnership,
                                                set_percentOwnership,
                                                linkTitle,
                                                set_linkTitle,
                                                isOfficer,
                                                set_isOfficer,
                                                employees,
                                            })}
                                            {LinkedEntitiesTab({
                                                title: tabNames[1],
                                                selectedTabIndex,
                                                principalId,
                                                entityId,
                                                linkStore,
                                            })}
                                            {ApplicationTab({
                                                title: tabNames[2],
                                                selectedTabIndex,
                                                principalId,
                                                applicationsStore,
                                            })}
                                            {BClientTab({
                                                title: tabNames[3],
                                                selectedTabIndex,
                                                principalId,
                                                bClientsStore,
                                            })}
                                            {UserAgreementsTab({
                                                title: tabNames[4],
                                                principalId,
                                            })}
                                            {CCLTab({
                                                isCCL_Borrower,
                                                set_isCCL_Borrower,
                                                requireTermsheet,
                                                set_requireTermsheet,
                                                requireUnderwritingFee,
                                                set_requireUnderwritingFee,
                                                requireValuationFee,
                                                set_requireValuationFee,
                                            })}
                                        </TabPanel>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='button'
                                            className='button primary'
                                            title='Save changes and close'
                                            disabled={selectedTabIndex > 0}
                                            onClick={validateAndSave}
                                        >
                                            Save & Exit
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={handleClose}
                                            title='Abandon changes and close'
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <ValidationMessagesModal
                open={!!validation}
                validations={validation}
                handleConfirm={onValidationConfirm}
                handleCancel={onValidationCancel}
            />
        </>
    );
};

export default EditPrincipalModal;
